import { useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import type { ActivityLevel, DietPattern } from "@/types";
import Step1Basics from "./Step1Basics";
import Step2Pregnancy from "./Step2Pregnancy";
import Step3Preferences from "./Step3Preferences";

export interface OnboardingDraft {
  name?: string;
  dateOfBirth?: string;
  prePregnancyWeightKg?: number;
  heightCm?: number;
  lmpDate?: string;
  dueDate?: string;
  conceptionDate?: string;
  isMultiples?: boolean;
  fetusCount?: number;
  hasGestationalDiabetes?: boolean;
  hasHypertension?: boolean;
  currentWeightKg?: number;
  activityLevel?: ActivityLevel;
  dietPattern?: DietPattern;
  allergies?: string[];
}

const STEP_LABELS = ["Basics", "Pregnancy", "Preferences"];

export default function OnboardingFlow() {
  const { refreshProfileStatus } = useAuth();
  const [step, setStep] = useState(0);
  const [draft, setDraft] = useState<OnboardingDraft>({});
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function advance(patch: Partial<OnboardingDraft>) {
    setDraft((d) => ({ ...d, ...patch }));
    setStep((s) => s + 1);
  }

  async function handleSubmit(patch: Partial<OnboardingDraft>) {
    const full = { ...draft, ...patch };
    setSubmitting(true);
    setError(null);
    try {
      await api.profile.create({
        dateOfBirth: full.dateOfBirth!,
        prePregnancyWeightKg: full.prePregnancyWeightKg!,
        heightCm: full.heightCm!,
        lmpDate: full.lmpDate!,
        dueDate: full.dueDate!,
        conceptionDate: full.conceptionDate ?? null,
        isMultiples: full.isMultiples ?? false,
        fetusCount: full.fetusCount ?? 1,
        activityLevel: full.activityLevel!,
        dietPattern: full.dietPattern!,
        allergies: full.allergies ?? [],
        hasGestationalDiabetes: full.hasGestationalDiabetes ?? false,
        hasHypertension: full.hasHypertension ?? false,
        lastNotifiedTrimester: null,
      });
      // Seed today's weight as the first weight_logs entry.
      if (full.currentWeightKg) {
        const today = new Date().toISOString().slice(0, 10);
        await api.logs.weight.upsert(today, full.currentWeightKg);
      }
      refreshProfileStatus();
    } catch {
      setError("Something went wrong saving your profile. Please try again.");
      setSubmitting(false);
    }
  }

  return (
    <div className="mx-auto flex min-h-dvh max-w-md flex-col px-6 py-10">
      <div className="mb-8 flex gap-2">
        {STEP_LABELS.map((label, i) => (
          <div key={label} className="flex-1">
            <div
              className={`h-1 rounded-pill ${i <= step ? "bg-forest-700" : "bg-line"}`}
            />
          </div>
        ))}
      </div>

      {step === 0 && <Step1Basics draft={draft} onNext={advance} />}
      {step === 1 && (
        <Step2Pregnancy draft={draft} onNext={advance} onBack={() => setStep(0)} />
      )}
      {step === 2 && (
        <Step3Preferences
          draft={draft}
          onSubmit={handleSubmit}
          onBack={() => setStep(1)}
          submitting={submitting}
        />
      )}

      {error && <p className="mt-4 text-center text-sm text-clay-500">{error}</p>}
    </div>
  );
}
